import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../config/config';
import {
  Box,
  Card,
  CardMedia,
  Container,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Tooltip from '@mui/material/Tooltip';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import { CircularProgress } from '@mui/material';
import Header from '../Hub/components/Header';
import PageHeading from '../../components/PageHeading';
import StandaloneBackButton from '../../components/StandaloneBackButton';
import { filterPanel, posterGrid } from '../../utils/surfaces';
import AddMovieModal from './components/AddMovieModal';
import EditMovieModal from './components/EditMovieModal';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';

interface Movie {
  id: string;
  title: string;
  year: number;
  image: string;
  type: string;
  wishlist?: boolean;
  order?: number;
}

/* ─────────────────────────────
 * Sortable wishlist card
 * ───────────────────────────── */
interface SortableWishlistCardProps {
  movie: Movie;
  onMenuClick: (e: React.MouseEvent<HTMLButtonElement>, movie: Movie) => void;
  onMoveToCollection: (movie: Movie) => void;
}

const SortableWishlistCard = ({ movie, onMenuClick, onMoveToCollection }: SortableWishlistCardProps) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="img"
            src={
              movie.type === '4K Ultra HD'
                ? '/logos/4k-ultra-hd.png'
                : '/logos/blu-ray.png'
            }
            alt={movie.type}
            sx={{
              height: 22,
              opacity: 0.9,
              borderRadius: 0.5,
              filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.4))',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Add to Collection" enterTouchDelay={0}>
            <IconButton size="small" onClick={() => onMoveToCollection(movie)}>
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={(e) => onMenuClick(e, movie)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <CardMedia
        component="img"
        src={movie.image}
        alt={movie.title}
        sx={{ width: '100%', aspectRatio: '2 / 3', objectFit: 'cover' }}
      />
    </Card>
  );
};

/* ─────────────────────────────
 * Page
 * ───────────────────────────── */
const PhysicalMovieCollection = () => {
  const [movies, setMovies] = useState<Movie[]>([]);
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);
  const [filterType, setFilterType] = useState<string>('4K Ultra HD');
  const [sortOption, setSortOption] = useState<string>('title-asc');
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState<'collection' | 'wishlist'>('collection');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /* ─────────────────────────────
   * Fetch movies
   * ───────────────────────────── */
  useEffect(() => {
    const fetchMovies = async () => {
      try {
        const ref = collection(db, 'PhysicalMovieCollection');
        const snapshot = await getDocs(ref);

        const list = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Movie[];

        setMovies(list);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMovies();
  }, []);

  /* ─────────────────────────────
   * Filter + sort (collection only)
   * ───────────────────────────── */
  useEffect(() => {
    let sorted = [...movies.filter((m) => !m.wishlist)];

    switch (sortOption) {
      case 'title-asc':
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-desc':
        sorted.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'year-asc':
        sorted.sort((a, b) => a.year - b.year);
        break;
      case 'year-desc':
        sorted.sort((a, b) => b.year - a.year);
        break;
    }

    const filtered =
      filterType === 'All'
        ? sorted
        : sorted.filter((m) => m.type === filterType);

    setFilteredMovies(filtered);
  }, [movies, sortOption, filterType]);

  const wishlistMovies = movies
    .filter((m) => m.wishlist === true)
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

  /* ─────────────────────────────
   * CRUD
   * ───────────────────────────── */
  const handleAddMovie = async (newMovie: Movie) => {
    const sameGroup = newMovie.wishlist
      ? movies.filter((m) => m.wishlist)
      : movies.filter((m) => !m.wishlist);

    const exists = sameGroup.find(
      (m) =>
        m.title.toLowerCase() === newMovie.title.toLowerCase() &&
        m.year === newMovie.year &&
        m.type === newMovie.type
    );

    if (exists) {
      toast.error(
        `"${newMovie.title}" is already in your ${newMovie.wishlist ? 'wishlist' : newMovie.type}`
      );
      return;
    }

    const movieToSave = newMovie.wishlist
      ? { ...newMovie, order: wishlistMovies.length }
      : newMovie;

    try {
      const ref = collection(db, 'PhysicalMovieCollection');
      const docRef = await addDoc(ref, movieToSave);
      setMovies([...movies, { ...movieToSave, id: docRef.id }]);
      toast.success(
        `Added "${newMovie.title}" to ${newMovie.wishlist ? 'wishlist' : 'collection'}`
      );
    } catch {
      toast.error('Failed to add movie.');
    }
  };

  const moveToCollection = async (movie: Movie) => {
    try {
      await updateDoc(doc(db, 'PhysicalMovieCollection', movie.id), { wishlist: false });
      setMovies((prev) =>
        prev.map((m) => (m.id === movie.id ? { ...m, wishlist: false } : m))
      );
      toast.success(`Moved "${movie.title}" to collection`);
    } catch {
      toast.error('Failed to move movie.');
    }
  };

  const handleMoveToCollection = async () => {
    if (!selectedMovie) return;
    await moveToCollection(selectedMovie);
    handleMenuClose();
  };

  const handleEditMovie = async (updated: Movie) => {
    try {
      const ref = doc(db, 'PhysicalMovieCollection', updated.id);
      await updateDoc(ref, updated as Partial<Movie>);
      setMovies((prev) =>
        prev.map((m) => (m.id === updated.id ? updated : m))
      );
      toast.success(`Updated "${updated.title}"`);
    } catch {
      toast.error('Failed to update movie.');
    }
  };

  const handleDeleteMovie = async () => {
    if (!selectedMovie) return;

    try {
      const ref = doc(db, 'PhysicalMovieCollection', selectedMovie.id);
      await deleteDoc(ref);
      setMovies((prev) => prev.filter((m) => m.id !== selectedMovie.id));
      setShowDeleteModal(false);
      toast.success(`Deleted "${selectedMovie.title}"`);
    } catch {
      toast.error('Failed to delete movie.');
    }
  };

  /* ─────────────────────────────
   * Menu
   * ───────────────────────────── */
  const handleMenuClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    movie: Movie
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedMovie(movie);
  };

  const handleMenuClose = () => setAnchorEl(null);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />

      <Container maxWidth="xl" sx={{ p: 3 }}>
        <ToastContainer />

        <StandaloneBackButton />
        <PageHeading>Physical Movie Collection</PageHeading>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="Collection" value="collection" />
          <Tab
            label={
              wishlistMovies.length > 0
                ? `Wishlist (${wishlistMovies.length})`
                : 'Wishlist'
            }
            value="wishlist"
          />
        </Tabs>

        {/* Filter bar — Collection only */}
        {activeTab === 'collection' && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              p: 2,
              mb: 3,
              ...filterPanel(theme),
            })}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortOption}
                label="Sort By"
                onChange={(e) => setSortOption(e.target.value)}
              >
                <MenuItem value="title-asc">Title (A–Z)</MenuItem>
                <MenuItem value="title-desc">Title (Z–A)</MenuItem>
                <MenuItem value="year-asc">Year ↑</MenuItem>
                <MenuItem value="year-desc">Year ↓</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Blu-Ray">Blu-Ray</MenuItem>
                <MenuItem value="4K Ultra HD">4K Ultra HD</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              sx={{ ml: 'auto' }}
              onClick={() => setShowAddModal(true)}
            >
              Add Movie
            </Button>
          </Box>
        )}

        {/* Wishlist toolbar */}
        {activeTab === 'wishlist' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="contained" onClick={() => setShowAddModal(true)}>
              Add to Wishlist
            </Button>
          </Box>
        )}

        {(() => {
          const displayMovies =
            activeTab === 'collection' ? filteredMovies : wishlistMovies;
          return (
            <Typography align="right" sx={{ mb: 2 }}>
              Showing {displayMovies.length}{' '}
              {displayMovies.length === 1 ? 'movie' : 'movies'}
            </Typography>
          );
        })()}

        {/* Movie grid */}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : activeTab === 'collection' ? (
          filteredMovies.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ mt: 6 }}>
              No movies found.
            </Typography>
          ) : (
            <Box sx={posterGrid}>
              {filteredMovies.map((movie) => (
                <Card key={movie.id} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      component="img"
                      src={
                        movie.type === '4K Ultra HD'
                          ? '/logos/4k-ultra-hd.png'
                          : '/logos/blu-ray.png'
                      }
                      alt={movie.type}
                      sx={{
                        height: 22,
                        opacity: 0.9,
                        borderRadius: 0.5,
                        filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.4))',
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, movie)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <CardMedia
                    component="img"
                    src={movie.image}
                    alt={movie.title}
                    sx={{
                      width: '100%',
                      aspectRatio: '2 / 3',
                      objectFit: 'cover',
                    }}
                  />
                </Card>
              ))}
            </Box>
          )
        ) : wishlistMovies.length === 0 ? (
          <Typography align="center" color="text.secondary" sx={{ mt: 6 }}>
            Your wishlist is empty.
          </Typography>
        ) : (
          <Box sx={posterGrid}>
                {wishlistMovies.map((movie) => (
                  <SortableWishlistCard
                    key={movie.id}
                    movie={movie}
                    onMenuClick={handleMenuClick}
                    onMoveToCollection={moveToCollection}
                  />
                ))}
              </Box>
        )}

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {selectedMovie?.wishlist ? (
            [
              <MenuItem key="move" onClick={handleMoveToCollection}>
                Move to Collection
              </MenuItem>,
              <MenuItem
                key="delete"
                sx={{ color: 'error.main' }}
                onClick={() => {
                  setShowDeleteModal(true);
                  handleMenuClose();
                }}
              >
                Delete
              </MenuItem>,
            ]
          ) : (
            [
              <MenuItem
                key="edit"
                onClick={() => {
                  setShowEditModal(true);
                  handleMenuClose();
                }}
              >
                Edit
              </MenuItem>,
              <MenuItem
                key="delete"
                sx={{ color: 'error.main' }}
                onClick={() => {
                  setShowDeleteModal(true);
                  handleMenuClose();
                }}
              >
                Delete
              </MenuItem>,
            ]
          )}
        </Menu>

        {/* Modals */}
        <AddMovieModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onAddMovie={handleAddMovie}
          wishlistMode={activeTab === 'wishlist'}
        />

        {selectedMovie && (
          <EditMovieModal
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            movie={selectedMovie}
            onEditMovie={handleEditMovie}
          />
        )}

        {selectedMovie && (
          <ConfirmDeleteModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteMovie}
            movieTitle={selectedMovie.title}
          />
        )}
      </Container>
    </Box>
  );
};

export default PhysicalMovieCollection;
